import { ONBOARDING_FORM_STYLES } from "@/features/onboarding/components/onboarding-form-styles";
import { cn } from "@/lib/utils";
import {
  Bell,
  BellRing,
  CalendarRange,
  MessageSquare,
  Settings,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PushNotificationService } from "@/common/utils/PushNotificationService";
import {
  NativeSettings,
  AndroidSettings,
  IOSSettings,
} from "capacitor-native-settings";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import * as Sentry from "@sentry/browser";

import { useState } from "react";
import { NotificationAudience } from "@/components/PushNotifications/notification-audience";

type Copy = {
  intro: string;
  footer: string;
  remindLaterIntro: string;
  benefits: {
    icon: typeof BellRing;
    title: string;
    description: string;
  }[];
  remindLaterItems: {
    icon: typeof BellRing;
    description: string;
  }[];
};

const PROVIDER_COPY: Copy = {
  intro:
    "Get instant push notifications for new shifts, credential reminders, and critical updates so you can act fast without checking the app all day.",
  footer:
    "You can adjust alert types anytime in settings. We only send messages when they help you secure work.",
  remindLaterIntro:
    "Enabling alerts keeps you ahead of other professionals and protects your credentials. If you turn them off, you could miss:",
  benefits: [
    {
      icon: BellRing,
      title: "Be first in line",
      description:
        "Instant alerts for new shifts that match your preferences so you can respond before other pros.",
    },
    {
      icon: CalendarRange,
      title: "Stay compliant effortlessly",
      description:
        "Helpful nudges before credentials expire, keeping paperwork on track without the scramble.",
    },
    {
      icon: ShieldCheck,
      title: "You stay in control",
      description:
        "Fine-tune or pause specific alerts anytime from settings. No spam—just what helps you work smarter.",
    },
  ],
  remindLaterItems: [
    {
      icon: BellRing,
      description: "Priority notifications when a new matching shift goes live",
    },
    {
      icon: CalendarRange,
      description: "Last-minute reminders before a credential expires",
    },
  ],
};

const ORG_COPY: Copy = {
  intro:
    "Get instant push notifications when candidates apply, providers message you, or offers are accepted — so your team can respond in minutes, not hours.",
  footer:
    "You can adjust alert types anytime in settings. We only send messages that help you hire and operate.",
  remindLaterIntro:
    "Enabling alerts helps your team respond before candidates move on. If you turn them off, you could miss:",
  benefits: [
    {
      icon: Users,
      title: "Respond first",
      description:
        "Instant alerts when candidates apply or accept offers so your roles fill faster.",
    },
    {
      icon: MessageSquare,
      title: "Keep conversations moving",
      description:
        "New chat messages from providers reach you immediately, no refreshing required.",
    },
    {
      icon: ShieldCheck,
      title: "You stay in control",
      description:
        "Fine-tune or pause specific alerts anytime from settings. No spam—just what helps your team operate.",
    },
  ],
  remindLaterItems: [
    {
      icon: Users,
      description:
        "Real-time alerts when candidates apply or accept your offers",
    },
    {
      icon: MessageSquare,
      description: "Chat messages from providers about active shifts",
    },
  ],
};

const ORG_MEMBER_COPY: Copy = {
  intro:
    "Stay up to date with messages, upcoming shifts, and credential reminders.",
  footer: "You can adjust your notification preferences anytime in settings.",
  remindLaterIntro:
    "You can enable notifications later in settings. Until then, check Saile for:",
  benefits: [
    {
      icon: MessageSquare,
      title: "Stay connected",
      description: "Receive messages and updates from your organization.",
    },
    {
      icon: CalendarRange,
      title: "Keep track of your schedule",
      description: "Get updates about your upcoming shifts.",
    },
    {
      icon: ShieldCheck,
      title: "Keep credentials current",
      description: "Receive reminders before your credentials expire.",
    },
  ],
  remindLaterItems: [
    {
      icon: MessageSquare,
      description: "Messages and schedule updates from your organization",
    },
    {
      icon: CalendarRange,
      description: "Credential expiration reminders",
    },
  ],
};

const COPY_BY_AUDIENCE: Record<NotificationAudience, Copy> = {
  [NotificationAudience.Provider]: PROVIDER_COPY,
  [NotificationAudience.Organization]: ORG_COPY,
  [NotificationAudience.OrganizationMember]: ORG_MEMBER_COPY,
};

export function PushNotificationsForm({
  onComplete,
  audience = NotificationAudience.OrganizationMember,
  appearance = "default",
}: {
  onComplete: (success: boolean) => void;
  audience?: NotificationAudience;
  appearance?: "default" | "onboarding";
}) {
  const isOnboarding = appearance === "onboarding";
  const [isBlocked, setIsBlocked] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const copy = COPY_BY_AUDIENCE[audience];

  const handleEnablePushNotifications = async () => {
    // Check if notifications are blocked
    let permissionStatus = null;
    try {
      permissionStatus = await PushNotificationService.requestPermission();
    } catch (error) {
      Sentry.captureException(error);
    }

    if (permissionStatus === false) {
      setIsBlocked(true);
      return;
    }

    // Complete if there is an error, don't block the user
    onComplete(true);
  };

  const openNotificationSettings = async () => {
    NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="space-y-2">
          <p
            className={
              isOnboarding
                ? "max-w-xl text-sm leading-6 text-slate-500 sm:text-base"
                : "max-w-xl text-sm text-gray-600"
            }
          >
            {copy.intro}
          </p>
        </div>
      </div>

      <ul className={isOnboarding ? "grid gap-3" : "grid gap-3 sm:grid-cols-2"}>
        {copy.benefits.map(({ icon: Icon, title, description }) => (
          <li
            key={title}
            className={
              isOnboarding
                ? "flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
                : "flex h-full items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            }
          >
            <span
              aria-hidden="true"
              className={
                isOnboarding
                  ? "flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary"
                  : "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600"
              }
            >
              <Icon className="h-4 w-4" />
            </span>
            <div className="space-y-1 text-left">
              <p className="font-medium text-gray-900">{title}</p>
              <p className="text-sm leading-6 text-slate-500">{description}</p>
            </div>
          </li>
        ))}
      </ul>

      {isBlocked && (
        <div className="rounded-xl border border-yellow-200 bg-yellow-50/90 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm">
            <Settings className="h-5 w-5 text-yellow-600" />
            <p className="font-medium text-yellow-800">
              Notifications are currently blocked
            </p>
          </div>
          <p className="mb-3 text-sm text-yellow-700">
            Enable notifications in your device settings to get important
            updates and reminders.
          </p>
          <Button
            variant="outline"
            size="lg"
            onClick={openNotificationSettings}
            className="w-full"
          >
            Open Notification Settings
          </Button>
        </div>
      )}

      <div className="space-y-3">
        <p
          className={
            isOnboarding
              ? "text-sm leading-6 text-slate-500"
              : "text-center text-sm text-gray-500"
          }
        >
          {copy.footer}
        </p>
        <div
          className={isOnboarding ? ONBOARDING_FORM_STYLES.footer : "space-y-3"}
        >
          <Button
            size="lg"
            className={
              isOnboarding
                ? ONBOARDING_FORM_STYLES.primaryButton
                : "group w-full gap-2 py-5 text-base transition-all duration-200"
            }
            onClick={handleEnablePushNotifications}
          >
            <Bell className="size-4 shrink-0" aria-hidden="true" />
            Enable Smart Alerts
          </Button>
          <div
            className={cn(
              "flex justify-center",
              isOnboarding && "sm:order-first",
            )}
          >
            <Button
              variant={isOnboarding ? "ghost" : "link"}
              size="none"
              className={
                isOnboarding
                  ? ONBOARDING_FORM_STYLES.secondaryButton
                  : "text-sm text-gray-500"
              }
              onClick={() => setShowConfirmDialog(true)}
            >
              Remind me later
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent
          className={
            isOnboarding
              ? ONBOARDING_FORM_STYLES.confirmation
              : "sm:max-w-[425px] w-[95vw] max-h-[95vh] overflow-y-auto"
          }
        >
          <DialogHeader>
            <DialogTitle className="text-xl">Prefer to wait?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-4 pt-3 text-left text-sm leading-6 text-slate-600">
                <p>{copy.remindLaterIntro}</p>
                <ul className="space-y-3">
                  {copy.remindLaterItems.map(
                    ({ icon: Icon, description }, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <Icon className="h-5 w-5 shrink-0 text-gray-600" />
                        <span className="text-sm text-gray-600">
                          {description}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:gap-2 mt-2">
            <Button
              variant="default"
              className={
                isOnboarding
                  ? ONBOARDING_FORM_STYLES.primaryButton
                  : "w-full sm:w-auto"
              }
              onClick={handleEnablePushNotifications}
            >
              Turn on alerts
            </Button>
            <Button
              variant="outline"
              className={
                isOnboarding
                  ? ONBOARDING_FORM_STYLES.secondaryButton
                  : "w-full sm:w-auto"
              }
              onClick={() => {
                setShowConfirmDialog(false);
                onComplete(false);
              }}
            >
              I'll opt out for now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
